import { useEffect, useRef, useState } from "react";
import Label from "../Label";
import Input from "../Input";
import DownArrowIcon from "../Icons/DownArrowIcon";
import MyCalendar from "../Calendar";
import { scrollIntoViewWithEase } from "../../helper/ScrollHelper";

const DateSelector = ({
  text,
  name,
  id,
  placeholder,
  onChange,
  selectedDate,
  disableFutureDates = true,
  disablePastDates = false,
  scrollInModal,
  error,
  setError,
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [inputValue, setInputValue] = useState("");
  // const calendarRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (selectedDate !== inputValue) {
      if (!selectedDate && inputValue.length > 0 && inputValue.length < 10) {
        return;
      }
      setInputValue(selectedDate || "");
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (scrollInModal && showCalendar && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    } else if (showCalendar && containerRef.current) {
      scrollIntoViewWithEase(containerRef.current);
    }
  }, [showCalendar, scrollInModal]);

  const formatDate = (date) => {
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  const parseDate = (dateString) => {
    const [day, month, year] = dateString.split("/");
    return new Date(year, month - 1, day);
  };

  // Handle manual typing
  const handleInputChange = (e) => {
    let value = e.target.value;
    setError("");

    // allow only numbers and /
    value = value.replace(/[^\d/]/g, "");

    // auto insert /
    if (value.length === 2 && inputValue.length === 1) {
      value += "/";
    }

    if (value.length === 5 && inputValue.length === 4) {
      value += "/";
    }

    if (value.length > 10) return;

    const parts = value.split("/");

    const day = parts[0];
    const month = parts[1];

    if (day && Number(day) > 31) return;

    if (month && Number(month) > 12) return;

    setInputValue(value);

    if (value.length === 10) {
      const [day, month, year] = value.split("/");
      const dateObj = new Date(year, month - 1, day);

      const isValidDate =
        dateObj.getFullYear() === Number(year) &&
        dateObj.getMonth() === Number(month) - 1 &&
        dateObj.getDate() === Number(day);

      if (!isValidDate) return;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (disableFutureDates && dateObj > today) {
        setError("Future dates are not allowed.");
        return;
      }

      if (disablePastDates && dateObj < today) {
        setError("Past dates are not allowed.");
        return;
      }

      setError("");
      onChange({ target: { value, name } });
    } else {
      if (selectedDate) {
        onChange({ target: { value: "", name } });
      }
    }
  };

  const handleOnChange = (date) => {
    const formattedDate = formatDate(date);
    setShowCalendar(false);
    onChange({ target: { value: formattedDate, name } });
  };

  useEffect(() => {
    if (selectedDate && selectedDate !== "Invalid date") {
      try {
        const parsedDate = parseDate(selectedDate);
        if (isNaN(parsedDate)) {
          onChange({ target: { value: "", name } });
        }
      } catch (e) {
        onChange({ target: { value: "", name } });
      }
    }
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div
        ref={containerRef}
        className={`relative rounded-xl cursor-pointer py-2 px-4 border ${
          error ? "date-select-error" : "date-select-default"
        }`}
        onClick={() => setShowCalendar(true)}
      >
        <Label
          htmlFor={name}
          id={id}
          className="date-select-lable"
          text={text}
        />
        <div className="relative">
          <Input
            type="text"
            name={name}
            id={id}
            className="date-select-input"
            placeholder={!selectedDate ? placeholder : ""}
            value={inputValue}
            onChange={handleInputChange}
          />
          <div className="absolute inset-y-0 -right-4 -top-8 flex items-center pr-3">
            <DownArrowIcon />
          </div>
        </div>

        {showCalendar && (
          <div className="absolute top-full mt-1 left-0 z-50 pb-5 min-w-[360px]">
            <MyCalendar
              disableFutureDates={disableFutureDates}
              disablePastDates={disablePastDates}
              onChange={handleOnChange}
              value={selectedDate ? parseDate(selectedDate) : null}
            />
          </div>
        )}
      </div>
      {error && <span className="text-error-500 text-sm">{error}</span>}
    </>
  );
};

export default DateSelector;
